import { useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { Department, DepartmentFormData } from '../types/department.types';
import { departmentApi } from '../api/departmentApi';

export const useDepartments = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch all departments
  const fetchDepartments = useCallback(async (): Promise<Department[]> => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await departmentApi.getAll();
      setDepartments(response.data);
      return response.data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch departments');
      setError(error);
      enqueueSnackbar(error.message, { variant: 'error' });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar]);

  // Fetch a single department by ID
  const fetchDepartmentById = useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await departmentApi.getById(id);
      setSelectedDepartment(data);
      return data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch department');
      setError(error);
      enqueueSnackbar(error.message, { variant: 'error' });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar]);

  // Create a new department
  const createDepartment = useCallback(async (departmentData: DepartmentFormData) => {
    try {
      setIsSubmitting(true);
      const newDepartment = await departmentApi.create(departmentData);
      setDepartments(prev => [...prev, newDepartment]);
      enqueueSnackbar('Department created successfully', { variant: 'success' });
      return newDepartment;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create department');
      enqueueSnackbar(error.message, { variant: 'error' });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [enqueueSnackbar]);

  // Update an existing department
  const updateDepartment = useCallback(async (id: string, departmentData: Partial<DepartmentFormData>) => {
    try {
      setIsSubmitting(true);
      const updatedDepartment = await departmentApi.update(id, departmentData);
      setDepartments(prev => 
        prev.map(dept => dept.id === id ? updatedDepartment : dept)
      );
      if (selectedDepartment?.id === id) {
        setSelectedDepartment(updatedDepartment);
      }
      enqueueSnackbar('Department updated successfully', { variant: 'success' });
      return updatedDepartment;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update department');
      enqueueSnackbar(error.message, { variant: 'error' });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [enqueueSnackbar, selectedDepartment]);

  // Delete a department
  const deleteDepartment = useCallback(async (id: string) => {
    try {
      setIsSubmitting(true);
      await departmentApi.delete(id);
      setDepartments(prev => prev.filter(dept => dept.id !== id));
      if (selectedDepartment?.id === id) {
        setSelectedDepartment(null);
      }
      enqueueSnackbar('Department deleted successfully', { variant: 'success' });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete department');
      enqueueSnackbar(error.message, { variant: 'error' });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [enqueueSnackbar, selectedDepartment]);

  // Toggle department status
  const toggleDepartmentStatus = useCallback(async (id: string, isActive: boolean) => {
    try {
      setIsSubmitting(true);
      const updatedDepartment = await departmentApi.updateStatus(id, { isActive });
      setDepartments(prev => 
        prev.map(dept => dept.id === id ? updatedDepartment : dept)
      );
      if (selectedDepartment?.id === id) {
        setSelectedDepartment(updatedDepartment);
      }
      enqueueSnackbar(
        `Department ${isActive ? 'activated' : 'deactivated'} successfully`, 
        { variant: 'success' }
      );
      return updatedDepartment;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update department status');
      enqueueSnackbar(error.message, { variant: 'error' });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [enqueueSnackbar, selectedDepartment]);

  return {
    departments,
    selectedDepartment,
    isLoading,
    isSubmitting,
    error,
    fetchDepartments,
    fetchDepartmentById,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    toggleDepartmentStatus,
    setSelectedDepartment,
  };
};
